<template>
    <modals-generic
    showFooter
    buttonFullWidth
    helperTextTitle
    :title="title"
    :buttonsFooterData="buttonsFooterData"
    :triggerClose="triggerClose"
    :scrollValue="scrollValue"
    @hideMe="emit('hideMe')"
    @clearScrollToValue="scrollValue = null">
        <div class="group-wrapper">
            <!-- *********************************************
            *                                                *
            *                    Personal Data               *
            *                                                *
            ********************************************** -->
            <divider-generic icon="identification" :title="t('join.createAccount.step4.generalInfo.personalDataTitle')" :applyBorder="false"/>
            <!-- Email -->
            <input-generic
            id="email"
            :params="emailParams"
            :inputValue="generalInfoForm.email"
            @input="[generalInfoForm.email = $event, clearFieldError('emailParams')]"
            @onBlur="emailBlurValidator"/>
            <!-- Number Of Children -->
            <input-generic
            id="numberOfChildren"
            :params="numberOfChildrenParams"
            :inputValue="generalInfoForm.numberOfChildren"
            @input="[generalInfoForm.numberOfChildren = $event, clearFieldError('numberOfChildrenParams')]"/>
            <!-- House Hold -->
            <input-generic
            id="houseHold"
            :params="houseHoldParams"
            :inputValue="generalInfoForm.houseHold"
            @input="[generalInfoForm.houseHold = $event, clearFieldError('houseHoldParams')]"
            @onBlur="houseHoldValidator"/>
            <!-- Literary Abilities -->
            <dropdown-generic
            id="literaryAbilities"
            :params="literaryAbilitiesParams"
            :options="getLiteraryAbilities"
            :selectedOption="generalInfoForm.literaryAbilities"
            @clicked="[generalInfoForm.literaryAbilities = $event, clearFieldError('literaryAbilitiesParams')]"/>
            <!-- *********************************************
            *                                                *
            *                 Professional Data              *
            *                                                *
            ********************************************** -->
            <divider-generic icon="briefcase" :title="t('join.createAccount.step4.generalInfo.professionalDataTitle')"/>
            <!-- Occupational Status -->
            <dropdown-generic
            id="occupationalStatus"
            :params="occupationalStatusParams"
            :options="getProcessOccupation"
            :selectedOption="generalInfoForm.occupationalStatus"
            @clicked="[generalInfoForm.occupationalStatus = $event, occupationalStatusHandler, clearFieldError('occupationalStatusParams')]"/>
            <!-- NIF Employer Entity -->
            <input-generic
            v-if="generalInfoForm.occupationalStatus === 3"
            id="nifEmployerEntity"
            :params="nifEmployerEntityParams"
            :inputValue="generalInfoForm.nifEmployerEntity"
            @input="[generalInfoForm.nifEmployerEntity = $event, clearFieldError('nifEmployerEntityParams')]"/>
            <!-- Employer Entity -->
            <input-generic
            v-if="generalInfoForm.occupationalStatus === 3"
            id="employerEntity"
            :params="employerEntityParams"
            :inputValue="generalInfoForm.employerEntity"
            @input="[generalInfoForm.employerEntity = $event, clearFieldError('employerEntityParams')]"/>
            <!-- Admission Date -->
            <input-calendar
            v-if="generalInfoForm.occupationalStatus === 3"
            id="admissionDate"
            :params="admissionDateParams"
            :disabledDates="{ from: new Date() }"
            :inputValue="formData.admissionDate"
            :errorState="admissionDateParams.errorState"
            @input="[generalInfoForm.admissionDate = $event, clearFieldError('admissionDateParams')]"/>
            <!-- Company Address -->
            <input-generic
            v-if="generalInfoForm.occupationalStatus === 3"
            id="companyAddress"
            :params="companyAddressParams"
            :inputValue="generalInfoForm.companyAddress"
            @input="[generalInfoForm.companyAddress = $event, clearFieldError('companyAddressParams')]"/>
            <!-- Company Door Number -->
            <input-generic
            v-if="generalInfoForm.occupationalStatus === 3"
            id="companyDoorNumber"
            :params="companyDoorNumberParams"
            :inputValue="generalInfoForm.companyDoorNumber"
            @input="[generalInfoForm.companyDoorNumber = $event, clearFieldError('companyDoorNumberParams')]"/>
            <!-- Company Floor Number -->
            <input-generic
            v-if="generalInfoForm.occupationalStatus === 3"
            id="companyFloorNumber"
            :params="companyFloorNumberParams"
            :inputValue="generalInfoForm.companyFloorNumber"
            @input="[generalInfoForm.companyFloorNumber = $event, clearFieldError('companyFloorNumberParams')]"/>
            <!-- Company Country -->
            <dropdown-generic
            v-if="generalInfoForm.occupationalStatus === 3"
            id="companyCountry"
            :params="companyCountryParams"
            :options="getCountries"
            :selectedOption="generalInfoForm.companyCountry"
            @clicked="[generalInfoForm.companyCountry = $event, clearFieldError('companyCountryParams'), clearFieldError('companyProvinceParams'), clearFieldError('companyCountyParams')]"/>
            <!-- Company Province -->
            <dropdown-generic
            v-if="generalInfoForm.occupationalStatus === 3 && generalInfoForm.companyCountry === 'Angola'"
            id="companyProvince"
            :params="companyProvinceParams"
            :options="getProvinces"
            :selectedOption="generalInfoForm.companyProvince"
            @clicked="[generalInfoForm.companyProvince = $event, clearFieldError('companyProvinceParams'), emit('requestCounties', $event)]"/>
            <!-- Company County -->
            <dropdown-generic
            v-if="generalInfoForm.occupationalStatus === 3 && showCountiesList && generalInfoForm.companyCountry === 'Angola'"
            id="companyCounty"
            :params="companyCountyParams"
            :options="getProvinceCounties"
            :selectedOption="generalInfoForm.companyCounty"
            @clicked="[generalInfoForm.companyCounty = $event, clearFieldError('companyCountyParams')]"/>
            <!-- Company Zip Code -->
            <input-generic
            v-if="generalInfoForm.occupationalStatus === 3 && generalInfoForm.companyCountry === 'Angola'"
            id="companyZipCode"
            :params="companyZipCodeParams"
            :inputValue="generalInfoForm.companyZipCode"
            @input="[generalInfoForm.companyZipCode = $event, clearFieldError('companyZipCodeParams')]"/>
            <!-- Upload for Occupational Status proof -->
            <div v-if="generalInfoForm.occupationalStatus && generalInfoForm.occupationalStatus !== 4" class="status-proof-wrapper">
                <div class="title">{{occupationalStatusTitle}}</div>
                <div class="instructions">{{occupationalStatusInstructions}}</div>
                <!-- File Uploader -->
                <file-uploader
                id="occupationalStatusProof"
                mandatory
                :autoUpload="false"
                :errorState="occupationalStatusProofParams.errorState"
                :errorMessage="occupationalStatusProofParams.errorMessage"
                :removeFile="removeFile"
                :initialFile="generalInfoForm.occupationalStatusProof?.file"
                :showSuccessFeedback="hasInitialFile"
                :label="t('join.createAccount.step4.generalInfo.proofUploadLabel')"
                :fileNote="t('join.createAccount.step4.generalInfo.proofUploadFileTypeNote')"
                @file-removed="removeFileHandler"
                @upload-success="uploadedFileHandler"
                @removeShowSuccessFeedback="removeFileHandler"/>
            </div>
            <!-- Profession -->
            <dropdown-generic
            id="profession"
            :params="professionParams"
            :options="getProfessions"
            :selectedOption="generalInfoForm.profession"
            @clicked="[generalInfoForm.profession = $event, clearFieldError('professionParams')]"/>
            <!-- Contractual Bond -->
            <dropdown-generic
            v-if="generalInfoForm.occupationalStatus === 3"
            id="contractualBond"
            :params="contractualBondParams"
            :options="getContractualTies"
            :selectedOption="generalInfoForm.contractualBond"
            @clicked="[generalInfoForm.contractualBond = $event, clearFieldError('contractualBondParams')]"/>
            <!-- Company Function -->
            <dropdown-generic
            v-if="generalInfoForm.occupationalStatus && generalInfoForm.occupationalStatus !== 4"
            id="companyFunction"
            :params="companyFunctionParams"
            :options="getProfessionalActivities"
            :selectedOption="generalInfoForm.companyFunction"
            @clicked="[generalInfoForm.companyFunction = $event, clearFieldError('companyFunctionParams')]"/>
            <!-- Job Description -->
            <input-generic
            v-if="generalInfoForm.occupationalStatus && generalInfoForm.occupationalStatus !== 4"
            id="jobDescription"
            :params="jobDescriptionParams"
            :inputValue="generalInfoForm.jobDescription"
            @input="generalInfoForm.jobDescription = $event"/>
            <!-- Monthly Income -->
            <input-amount
            id="monthlyIncome"
            :params="monthlyIncomeParams"
            :inputValue="formData.monthlyIncome"
            @onBlur="monthlyIncomeValidator"
            @input="[generalInfoForm.monthlyIncome = $event, clearFieldError('monthlyIncomeParams')]"/>
            <!-- Monthly Expenses -->
            <input-amount
            id="monthlyExpenses"
            :params="monthlyExpensesParams"
            :inputValue="formData.monthlyExpenses"
            @onBlur="monthlyExpensesValidator"
            @input="[generalInfoForm.monthlyExpenses = $event, clearFieldError('monthlyExpensesParams')]"/>
            <!-- Corporate Interests -->
            <selector-radio-group
            id="corporateInterests"
            :mandatory="corporateInterestsParams.mandatory"
            :label="corporateInterestsParams.label"
            :errorState="corporateInterestsParams.errorState"
            :errorMessage="corporateInterestsParams.errorMessage"
            :radioParams="corporateInterestsRadioParamsOptions"
            @clicked="corporateInterestsRadioHandler"/>
        </div>
        <!-- Corporate Interests List -->
        <div v-if="generalInfoForm.corporateInterests === 'Yes'" class="corporate-interest-list-wrapper prevent-auto-margins">
            <!-- Title -->
            <divider-generic
            icon="building"
            dividerSize="small"
            :applyBorder="false"
            :title="t('join.createAccount.step4.generalInfo.corporateInterestsListTitle')"/>
            <!-- Instructions on how to add -->
            <div id="shareHoldingsList" class="instructions">{{t('join.createAccount.step4.generalInfo.corporateInterestsListInstructions')}}</div>
            <!-- Corporate Interest Share Holding List -->
            <table-generic
            v-if="generalInfoForm.shareHoldingsList.length"
            :hasDetail="false"
            :headerData="shareHoldingsTableHeaderData"
            :tableStyles="shareHoldingsTableStyles"
            :tableData="shareHoldingsTableData"
            @moreOptionsClicked="moreOptionsClicked"/>
            <!-- Button to Add more Corporate Interest -->
            <button-secondary
            icon="plus"
            buttonSize="small"
            :hasError="shareHoldingsListParams.errorState"
            :label="t('join.createAccount.step4.generalInfo.corporateInterestAddBtn')"
            @clicked="showAddCorportareInterestModal = true"/>
            <!-- Error Message if corporateInterests has value yes, but no shareHoldings exist -->
            <div v-if="shareHoldingsListParams.errorState" class="error-message">{{shareHoldingsListParams.errorMessage}}</div>
        </div>
    </modals-generic>
    <!-- Corporate Interests Add Modal -->
    <corporate-interests-modal
    v-if="showAddCorportareInterestModal"
    :index="selectedCorporateInfo.index"
    :corporateInterestInfo="selectedCorporateInfo.info"
    @updateCorporateInterestInfo="updateCorporateInterestInfo"
    @hideMe="showAddCorportareInterestModal = false"/>
    <!-- Remove Corporate Interest Alert -->
    <modal-centered-generic
    v-if="showAlertRemoveCorporateInterest"
    negative
    rightButtonIcon="check-line"
    :title="t('join.createAccount.step4.generalInfo.corporateInterestRemoveTitle')"
    :text="t('join.createAccount.step4.generalInfo.corporateInterestRemoveText')"
    :leftButtonText="t('join.createAccount.step4.generalInfo.corporateInterestNoBtn')"
    :rightButtonText="t('join.createAccount.step4.generalInfo.corporateInterestRemoveBtn')"
    :triggerClose="triggerCloseAlertRemoveCorporateInterest"
    @hideMe="[showAlertRemoveCorporateInterest = false, triggerCloseAlertRemoveCorporateInterest = false]"
    @leftButtonClicked="clickedCorporateInterest = null"
    @rightButtonClicked="removeCorpotateInterest"/>
    <!-- Remove All Corporate Interest Alert -->
    <modal-centered-generic
    v-if="showAlertRemoveAllCorporateInterest"
    negative
    rightButtonIcon="check-line"
    :title="t('join.createAccount.step4.generalInfo.corporateInterestRemoveAllTitle')"
    :text="t('join.createAccount.step4.generalInfo.corporateInterestRemoveAllText')"
    :leftButtonText="t('join.createAccount.step4.generalInfo.corporateInterestNoBtn')"
    :rightButtonText="t('join.createAccount.step4.generalInfo.corporateInterestRemoveBtn')"
    :triggerClose="triggerCloseAlertRemoveCorporateInterest"
    @hideMe="[showAlertRemoveAllCorporateInterest = false, triggerCloseAlertRemoveCorporateInterest = false]"
    @rightButtonClicked="corporateInterestsRadioRemove"/>
    <!-- Feedback Toasts -->
    <toasts-generic
	v-if="showFeedbackToast"
	:toastType="feedbackToastData.toastType"
	:feedbackText="feedbackToastData.text"
	:buttonsParams="feedbackToastData.buttons"
	@hideMe="showFeedbackToast = false"/>
</template>

<script setup>
import ModalsGeneric from '@/components/generic/modals/side/Generic'
import DividerGeneric from '@/components/generic/dividers/Generic'
import InputGeneric from '@/components/generic/inputs/Generic'
import InputCalendar from '@/components/generic/inputs/Calendar'
import DropdownGeneric from '@/components/generic/dropdowns/generic/Generic'
import FileUploader from '@/components/generic/uploader/FileUploader'
import InputAmount from '@/components/generic/inputs/Amount'
import SelectorRadioGroup from '@/components/generic/selectors/radio/RadioGroup'
import CorporateInterestsModal from '@/components/features/nonAuthenticated/join/createAccount/step4Modals/CorporateInterests'
import TableGeneric from '@/components/generic/table/Generic'
import ButtonSecondary from '@/components/generic/buttons/Secondary'
import ModalCenteredGeneric from '@/components/generic/modals/centered/Generic'
import ToastsGeneric from '@/components/generic/toasts/Generic'

/* Vue & Helpers */
import { ref, computed, watch } from 'vue'
import { ACCOUNT_MAIN_CURRENCY } from '@/assets/constants/accounts'
import { NUMBERS_ONLY } from '@/assets/constants/masks'
import { emailValidator, removeAccents, onlyNumbersNoSpace, postalCode } from '@/assets/scripts/validators'
import { getEmptyFieldsIdentifiers } from '@/assets/scripts/objects'
import { getElementOffsetTopPosition } from '@/assets/scripts/htmlManipulators'
import { errorObjectConstructor, emptyObjectConstructor } from '@/assets/scripts/toastsHelpers'

/* Store */
import { mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['hideMe', 'updateModalInfo', 'validateCompanyNIFHandler', 'requestCounties'])

/** Props
@title {String} - title for our modal
@formData {Object} - Object with form data
@companyNIFInfo {Object} - Company Info and prop telling if service could validate NIF
@showCountiesList {Boolean}
@mandatoryList {Array} - List of mandatory fields ids/keys
*/
const props = defineProps({
    title: String,
    formData: Object,
    companyNIFInfo: Object,
    showCountiesList: Boolean,
    mandatoryList: Array
})

const {
    getLiteraryAbilities,
    getCountries,
    getProvinces,
    getProvinceCounties,
    getProcessOccupation,
    getProfessions,
    getProfessionalActivities,
    getContractualTies
} = mapGetters(moduleMapper.JOIN_CREATE_ACCOUNT)

/* Data */
const triggerClose = ref(false)
const scrollValue = ref(0)

const hasInitialFile = ref(props.formData.occupationalStatusProof)

const generalInfoForm = ref({
    email: props.formData.email,
    numberOfChildren: props.formData.numberOfChildren,
    houseHold: props.formData.houseHold,
    literaryAbilities: props.formData.literaryAbilities,
    occupationalStatus: props.formData.occupationalStatus,
    nifEmployerEntity: props.formData.nifEmployerEntity,
    employerEntity: props.formData.employerEntity,
    admissionDate: props.formData.admissionDate,
    companyAddress: props.formData.companyAddress,
    companyDoorNumber: props.formData.companyDoorNumber,
    companyFloorNumber: props.formData.companyFloorNumber,
    companyCountry: props.formData.companyCountry,
    companyProvince: props.formData.companyProvince,
    companyCounty: props.formData.companyCounty,
    companyZipCode: props.formData.companyZipCode,
    occupationalStatusProof: props.formData.occupationalStatusProof,
    profession: props.formData.profession,
    contractualBond: props.formData.contractualBond,
    companyFunction: props.formData.companyFunction,
    jobDescription: props.formData.jobDescription,
    monthlyIncome: props.formData.monthlyIncome,
    monthlyExpenses: props.formData.monthlyExpenses,
    corporateInterests: props.formData.corporateInterests,
    shareHoldingsList: props.formData.shareHoldingsList
})

watch(() => props.companyNIFInfo, (newVal, oldVal) => {
    if (props.companyNIFInfo) {
        // TODO WHEN API RETURNS INFO
        // generalInfoForm.value.companyAddress = props.companyNIFInfo.companyAddress
        // generalInfoForm.value.companyDoorNumber = props.companyNIFInfo.companyDoorNumber
        // generalInfoForm.value.companyFloorNumber = props.companyNIFInfo.companyFloorNumber
        // generalInfoForm.value.companyCountry = props.companyNIFInfo.companyCountry
        // generalInfoForm.value.companyProvince = props.companyNIFInfo.companyProvince
        // generalInfoForm.value.companyCounty = props.companyNIFInfo.companyCounty
    }
})

/*************************************************
*                                                *
*                  Personal Data                 *
*                                                *
*************************************************/
/*************************************************
*                      Email                     *
*************************************************/
const emailParams = ref({
    labelText: t('join.createAccount.step4.generalInfo.email'),
    placeholder: t('join.createAccount.step4.generalInfo.emailPlaceholder'),
    errorState: false,
    errorMessage: null
})

const emailBlurValidator = () => {
    if (generalInfoForm.value.email.length && !emailValidator(generalInfoForm.value.email)) {
        emailParams.value.errorState = true
        emailParams.value.errorMessage = t('invalidEmailError')
    }
}

/*************************************************
*                Number Of Children              *
*************************************************/
const numberOfChildrenParams = ref({
    labelText: t('join.createAccount.step4.generalInfo.numberOfChildren'),
    placeholder: t('join.createAccount.step4.generalInfo.numberOfChildrenPlaceholder'),
    mandatory: true,
    maxLength: 2,
    mask: NUMBERS_ONLY,
    applyNumbersIntInputMode: true,
    errorState: false,
    errorMessage: null
})

/*************************************************
*                    House Hold                  *
*************************************************/
const houseHoldParams = ref({
    labelText: t('join.createAccount.step4.generalInfo.houseHold'),
    placeholder: t('join.createAccount.step4.generalInfo.houseHoldPlaceholder'),
	mandatory: true,
    maxLength: 2,
    mask: NUMBERS_ONLY,
    applyNumbersIntInputMode: true,
    errorState: false,
    errorMessage: null
})

const houseHoldValidator = () => {
    if (generalInfoForm.value.houseHold.length && Number(generalInfoForm.value.houseHold) === 0) {
        houseHoldParams.value.errorState = true
        houseHoldParams.value.errorMessage = t('join.createAccount.step4.generalInfo.houseHoldError')
    }
}

/*************************************************
 *                Literary Abilities             *
 *************************************************/
 const literaryAbilitiesParams = ref({
    labelText: t('join.createAccount.step4.generalInfo.literaryAbilities'),
    placeholder: t('join.createAccount.step4.generalInfo.literaryAbilitiesPlaceholder'),
	mandatory: true,
    errorState: false,
    errorMessage: null
})

/*************************************************
*                                                *
*                Professional Data               *
*                                                *
*************************************************/
/*************************************************
 *               Occupational Status             *
 *************************************************/
 const occupationalStatusParams = ref({
    labelText: t('join.createAccount.step4.generalInfo.occupationalStatus'),
    placeholder: t('join.createAccount.step4.generalInfo.occupationalStatusPlaceholder'),
	mandatory: true,
    errorState: false,
    errorMessage: null
})

watch(() => generalInfoForm.value.occupationalStatus, (newVal, oldVal) => {
    /* Selecting specific companyFunction if student */
    if (generalInfoForm.value.occupationalStatus === 1) {
        generalInfoForm.value.companyFunction = 'ESTUDANTE'
        generalInfoForm.value.profession = 'Estudante'

        clearFieldError('companyFunctionParams')
        clearFieldError('professionParams')
    }

    /* If occupationalStatus is not OtherEmployed, we need to clear specific fields mandatory error */
    if (generalInfoForm.value.occupationalStatus !== 3) {
        clearFieldError('nifEmployerEntityParams')
        clearFieldError('employerEntityParams')
        clearFieldError('admissionDateParams')
        clearFieldError('companyAddressParams')
        clearFieldError('companyDoorNumberParams')
        clearFieldError('companyFloorNumberParams')
        clearFieldError('companyCountryParams')
        clearFieldError('companyProvinceParams')
        clearFieldError('companyCountyParams')
        clearFieldError('companyZipCodeParams')
    }

    /* If occupationalStatus is unemployed, function is not mandatory */
    if (generalInfoForm.value.occupationalStatus === 4) {
        clearFieldError('companyFunctionParams')
        clearFieldError('occupationalStatusProofParams')
    }
})

/*************************************************
*               NIF Employer Entity             *
*************************************************/
const previousSearchedNIF = ref(null)
const nifEmployerEntityParams = ref({
    labelText: t('join.createAccount.step4.generalInfo.nifEmployerEntity'),
    placeholder: t('join.createAccount.step4.generalInfo.nifEmployerEntityPlaceholder'),
	mandatory: true,
    errorState: false,
    errorMessage: null,
    maxLength: 10,
    ourMask: {
        validatorFn: (value) => { return onlyNumbersNoSpace(value) }
    }
})

watch(() => generalInfoForm.value.nifEmployerEntity, (newVal, oldVal) => {
    if (newVal !== previousSearchedNIF.value && generalInfoForm.value.nifEmployerEntity.length === 10) {
        previousSearchedNIF.value = newVal
        emit('validateCompanyNIFHandler', generalInfoForm.value.nifEmployerEntity)
    }
})

/*************************************************
 *                 Employer Entity               *
 *************************************************/
 const employerEntityParams = ref({
    labelText: t('join.createAccount.step4.generalInfo.employerEntity'),
    placeholder: t('join.createAccount.step4.generalInfo.employerEntityPlaceholder'),
	mandatory: true,
    errorState: false,
    errorMessage: null,
    ourMask: {
        parseInputText: value => { return value.toUpperCase() }
    }
})

/*************************************************
 *                 Admission Date               *
 *************************************************/
 const admissionDateParams = ref({
    labelText: t('join.createAccount.step4.generalInfo.admissionDate'),
    placeholder: t('join.createAccount.step4.generalInfo.admissionDate'),
	mandatory: true,
    errorState: false,
    errorMessage: null
})

/*************************************************
 *                 Company Address               *
 *************************************************/
 const companyAddressParams = ref({
    labelText: t('join.createAccount.step4.generalInfo.companyAddress'),
    placeholder: t('join.createAccount.step4.generalInfo.companyAddressPlaceholder'),
	mandatory: true,
    errorState: false,
    errorMessage: null,
    ourMask: {
        parseInputText: value => { return value.toUpperCase() }
    }
})

/*************************************************
 *               Company Door Number             *
 *************************************************/
 const companyDoorNumberParams = ref({
    labelText: t('join.createAccount.step4.generalInfo.companyDoorNumber'),
    placeholder: t('join.createAccount.step4.generalInfo.companyDoorNumberPlaceholder'),
	mandatory: true,
    errorState: false,
    errorMessage: null,
    ourMask: {
        parseInputText: value => { return value.toUpperCase() }
    }
})

/*************************************************
 *               Company Floor Number            *
 *************************************************/
 const companyFloorNumberParams = ref({
    labelText: t('join.createAccount.step4.generalInfo.companyFloorNumber'),
    placeholder: t('join.createAccount.step4.generalInfo.companyFloorNumberPlaceholder'),
	mandatory: true,
    errorState: false,
    errorMessage: null,
    ourMask: {
        parseInputText: value => { return value.toUpperCase() }
    }
})

/*************************************************
 *                 Company Country               *
 *************************************************/
 const companyCountryParams = ref({
    labelText: t('join.createAccount.step4.generalInfo.companyCountry'),
    placeholder: t('join.createAccount.step4.generalInfo.companyCountryPlaceholder'),
    withSearch: true,
	mandatory: true,
    errorState: false,
    errorMessage: null
})

/*************************************************
 *                 Company Province              *
 *************************************************/
 const companyProvinceParams = ref({
    labelText: t('join.createAccount.step4.generalInfo.companyProvince'),
    placeholder: t('join.createAccount.step4.generalInfo.companyProvincePlaceholder'),
    withSearch: true,
	mandatory: true,
    errorState: false,
    errorMessage: null
})

/*************************************************
 *                  Company County               *
 *************************************************/
 const companyCountyParams = ref({
    labelText: t('join.createAccount.step4.generalInfo.companyCounty'),
    placeholder: t('join.createAccount.step4.generalInfo.companyCountyPlaceholder'),
    withSearch: true,
	mandatory: true,
    errorState: false,
    errorMessage: null
})

/*************************************************
*                 Company Zip Code               *
*************************************************/
const companyZipCodeParams = ref({
    labelText: t('join.createAccount.step4.generalInfo.companyZipCode'),
    placeholder: t('join.createAccount.step4.generalInfo.companyZipCodePlaceholder'),
    errorState: false,
    errorMessage: null,
    applyNumbersIntInputMode: true,
    maxLength: 12,
    mandatory: true,
    ourMask: {
        validatorFn: (value) => { return postalCode(value) },
        parseInputText: (value) => { return removeAccents(value) }
    }
})

/*************************************************
*             Occupational Status proof          *
*************************************************/
const occupationalStatusTitle = computed(() => {
    const titles = {
        1: t('join.createAccount.step4.generalInfo.studentProofTitle'),
        2: t('join.createAccount.step4.generalInfo.selfEmployedProofTitle'),
        3: t('join.createAccount.step4.generalInfo.otherEmployedProofTitle')
    }
    return titles[generalInfoForm.value.occupationalStatus]
})

const occupationalStatusInstructions = computed(() => {
    const instructions = {
        1: t('join.createAccount.step4.generalInfo.studentProofInstructions'),
        2: t('join.createAccount.step4.generalInfo.selfEmployedProofInstructions'),
        3: t('join.createAccount.step4.generalInfo.otherEmployedProofInstructions')
    }
    return instructions[generalInfoForm.value.occupationalStatus]
})

const occupationalStatusProofParams = ref({
    errorState: false,
    errorMessage: null
})

const removeFile = ref(false)

const removeFileHandler = () => {
    hasInitialFile.value = null
    removeFile.value = false
    generalInfoForm.value.occupationalStatusProof = null
}

const readFile = file => {
    if (!file.type.includes('pdf')) {
        feedbackToastData.value = errorObjectConstructor({
            message: t('join.createAccount.step4.generalInfo.invalidFileError'),
            dismissToastBtnText: t('commonButtons.gotIt')
        })
        showFeedbackToast.value = true
        removeFile.value = true
        return
    }

	if (file) { // we only run if there is actually a file
		var reader = new FileReader() // initiate a new file reader
		reader.onload = async function (e) { // callback for the reader
			var base64Img = e.target.result // gets the image in base 64
			var binaryImg = convertDataURIToBinary(base64Img) // converts base 64 to binary
			var blob = new Blob([binaryImg], { type: file.type }) // creates the blob with the binary
			var fileName = file.name // get the name of tile
			var readyFile = { blob: blob, name: fileName, fileType: file.type?.split('/')[1] }
			fileReadComplete(readyFile)
		}
		reader.readAsDataURL(file) // starts the reading
	}
}

const fileReadComplete = fileInfo => {
    /* finding fileType in our fileName */
    const temp = fileInfo.name.split('.')
    /* blob -> fileInfo.blob */
    generalInfoForm.value.occupationalStatusProof.fileBlob = fileInfo.blob
    /* fileName -> fileInfo.name */
    generalInfoForm.value.occupationalStatusProof.fileName = temp[0]
    /* fileType -> fileInfo.fileType */
    generalInfoForm.value.occupationalStatusProof.fileType = fileInfo.fileType
}

/**
* aux function to convert a DATAURI to BINARY
*/
const convertDataURIToBinary = dataURI => {
	var BASE64_MARKER = ';base64,'
	var base64Index = dataURI.indexOf(BASE64_MARKER) + BASE64_MARKER.length
	var base64 = dataURI.substring(base64Index)
	var raw = window.atob(base64)
	var rawLength = raw.length
	var array = new Uint8Array(new ArrayBuffer(rawLength))
	for (var i = 0; i < rawLength; i++) {
		array[i] = raw.charCodeAt(i)
	}
	return array
}

const uploadedFileHandler = $event => {
    generalInfoForm.value.occupationalStatusProof = { file: $event }
    showFeedbackToast.value = false
    readFile($event)
    clearFieldError('occupationalStatusProofParams')
}

/*************************************************
*                                                *
*             Success/Error Handlers             *
*                                                *
*************************************************/
const showFeedbackToast = ref(false)
const feedbackToastData = ref(emptyObjectConstructor())

/*************************************************
*                    Profession                 *
*************************************************/
const professionParams = ref({
    labelText: t('join.createAccount.step4.generalInfo.profession'),
    placeholder: t('join.createAccount.step4.generalInfo.professionPlaceholder'),
    withSearch: true,
	mandatory: true,
    errorState: false,
    errorMessage: null
})

/*************************************************
*                  Contractual Bond              *
*************************************************/
const contractualBondParams = ref({
    labelText: t('join.createAccount.step4.generalInfo.contractualBond'),
    placeholder: t('join.createAccount.step4.generalInfo.contractualBondPlaceholder'),
	mandatory: true,
    errorState: false,
    errorMessage: null
})

/*************************************************
*                 Company Function               *
*************************************************/
const companyFunctionParams = ref({
    labelText: t('join.createAccount.step4.generalInfo.companyFunction'),
    placeholder: t('join.createAccount.step4.generalInfo.companyFunctionPlaceholder'),
	mandatory: true,
    errorState: false,
    errorMessage: null
})

/*************************************************
*                  Job Description               *
*************************************************/
const jobDescriptionParams = ref({
    labelText: t('join.createAccount.step4.generalInfo.jobDescription'),
    placeholder: t('join.createAccount.step4.generalInfo.jobDescriptionPlaceholder'),
    maxLength: 40
})

/*************************************************
*                  Annual Income                 *
*************************************************/
const monthlyIncomeParams = ref({
    labelText: t('join.createAccount.step4.generalInfo.monthlyIncome'),
	mandatory: true,
    currency: ACCOUNT_MAIN_CURRENCY,
    mask: NUMBERS_ONLY,
    errorState: false,
    errorMessage: null
})

const monthlyIncomeValidator = () => {
    if (generalInfoForm.value.monthlyIncome.length && !Number(generalInfoForm.value.monthlyIncome)) {
		monthlyIncomeParams.value.errorState = true
		monthlyIncomeParams.value.errorMessage = t('invalidAmount')
	}
}

/*************************************************
*                  Monthly Expenses                 *
*************************************************/
const monthlyExpensesParams = ref({
    labelText: t('join.createAccount.step4.generalInfo.monthlyExpenses'),
	mandatory: true,
    currency: ACCOUNT_MAIN_CURRENCY,
    mask: NUMBERS_ONLY,
    errorState: false,
    errorMessage: null
})

const monthlyExpensesValidator = () => {
    if (generalInfoForm.value.monthlyExpenses.length && !Number(generalInfoForm.value.monthlyExpenses)) {
		monthlyExpensesParams.value.errorState = true
		monthlyExpensesParams.value.errorMessage = t('invalidAmount')
	}
}

/*************************************************
*                Corporate Interest              *
*************************************************/
const showAddCorportareInterestModal = ref(false)

const corporateInterestsParams = ref({
    label: t('join.createAccount.step4.generalInfo.corporateInterests'),
	mandatory: true,
    errorState: false,
    errorMessage: null
})

const corporateInterestsRadioParamsOptions = computed(() => {
    return [{
        id: 'Yes',
        text: t('join.createAccount.step4.generalInfo.corporateInterestsYes'),
        value: 'Yes',
        selected: generalInfoForm.value.corporateInterests === 'Yes'
    }, {
        id: 'No',
        text: t('join.createAccount.step4.generalInfo.corporateInterestsNo'),
        value: 'No',
        selected: generalInfoForm.value.corporateInterests === 'No'
    }]
})

const corporateInterestsRadioHandler = $event => {
    if ($event === 'No') {
        if (generalInfoForm.value?.shareHoldingsList.length) {
            showAlertRemoveAllCorporateInterest.value = true
        } else {
            generalInfoForm.value.corporateInterests = $event
            clearFieldError('shareHoldingsListParams')
            clearFieldError('corporateInterestsParams')
        }
    } else {
        generalInfoForm.value.corporateInterests = $event
        clearFieldError('corporateInterestsParams')
    }
}

const corporateInterestsRadioRemove = () => {
    generalInfoForm.value.corporateInterests = 'No'
    generalInfoForm.value.shareHoldingsList = []
    triggerCloseAlertRemoveCorporateInterest.value = true
    clearFieldError('shareHoldingsListParams')
    clearFieldError('corporateInterestsParams')
}

/*************************************************
*        Corporate Interest Share Holdings       *
*************************************************/
const selectedCorporateInfo = ref({})
const showAlertRemoveCorporateInterest = ref(false)
const showAlertRemoveAllCorporateInterest = ref(false)
const clickedCorporateInterest = ref(null)
const triggerCloseAlertRemoveCorporateInterest = ref(false)

const shareHoldingsListParams = ref({
    errorState: false,
    errorMessage: null
})

const updateCorporateInterestInfo = ({ info, index }) => {
    if (!index && index !== 0) generalInfoForm.value.shareHoldingsList.push(info)
    else generalInfoForm.value.shareHoldingsList[index] = info
}

const shareHoldingsTableHeaderData = [
    {
        label: '',
        rowCellType: 'title',
        showInMobile: { groupWith: ['interestsNif_interestsCountry'] }
    },
    { label: '', rowCellType: 'description', dataAttr: 'interestsName_interestsPercentageOfCapital' },
    { dataAttr: 'tableOptions', rowCellType: 'more-options', showInMobile: true }
]

const shareHoldingsTableStyles = [null, null, null]

const tableMoreOptions = [{
    id: 1,
    text: t('join.createAccount.step4.generalInfo.shareHoldingsMoreOptions.edit'),
    icon: 'pencil',
    callbackFunction: (rowId) => {
        selectedCorporateInfo.value = {
            index: rowId,
            info: generalInfoForm.value.shareHoldingsList[rowId]
        }
        showAddCorportareInterestModal.value = true
    }
}, {
    id: 2,
    text: t('join.createAccount.step4.generalInfo.shareHoldingsMoreOptions.delete'),
    icon: 'delete',
    variant: 'negative',
    callbackFunction: (rowId) => {
        showAlertRemoveCorporateInterest.value = true
        clickedCorporateInterest.value = rowId
    }
}]

const removeCorpotateInterest = () => {
    generalInfoForm.value.shareHoldingsList.splice(clickedCorporateInterest.value, 1)
    clickedCorporateInterest.value = null
    triggerCloseAlertRemoveCorporateInterest.value = true
}

const shareHoldingsTableData = computed(() => {
    return generalInfoForm.value.shareHoldingsList.map((corporate, index) => {
        return [
            { value: index, dataAttr: 'id' },
            { type: 'title', value: `${corporate.interestsName} (${corporate.interestsPercentageOfCapital}%)`, dataAttr: 'interestsName_interestsPercentageOfCapital' },
            { type: 'description', value: `NIF: ${corporate.interestsNif} - ${corporate.interestsCountry}`, dataAttr: 'interestsNif_interestsCountry' },
            { type: 'more-options', value: tableMoreOptions, dataAttr: 'tableOptions' }
        ]
    })
})

const moreOptionsClicked = row => {
	tableMoreOptions.find(obj => obj.id === row.optionClicked).callbackFunction(row.rowId)
}

/**********************************************************************
*                                                                     *
*                          Mandatory Fields                           *
*                                                                     *
***********************************************************************/
const allFormFields = ref({
    emailParams: emailParams.value,
    numberOfChildrenParams: numberOfChildrenParams.value,
    houseHoldParams: houseHoldParams.value,
    literaryAbilitiesParams: literaryAbilitiesParams.value,
    occupationalStatusParams: occupationalStatusParams.value,
    nifEmployerEntityParams: nifEmployerEntityParams.value,
    employerEntityParams: employerEntityParams.value,
    admissionDateParams: admissionDateParams.value,
    companyAddressParams: companyAddressParams.value,
    companyDoorNumberParams: companyDoorNumberParams.value,
    companyFloorNumberParams: companyFloorNumberParams.value,
    companyCountryParams: companyCountryParams.value,
    companyProvinceParams: companyProvinceParams.value,
    companyCountyParams: companyCountyParams.value,
    companyZipCodeParams: companyZipCodeParams.value,
    occupationalStatusProofParams: occupationalStatusProofParams.value,
    professionParams: professionParams.value,
    contractualBondParams: contractualBondParams.value,
    companyFunctionParams: companyFunctionParams.value,
    monthlyIncomeParams: monthlyIncomeParams.value,
    monthlyExpensesParams: monthlyExpensesParams.value,
    corporateInterestsParams: corporateInterestsParams.value,
    shareHoldingsListParams: shareHoldingsListParams.value
})

/*
* We have all mandatory fields here,
* but we comment the ones that depend on some option
*/
const mandatoryList = ref([
    'numberOfChildren',
    'houseHold',
    'literaryAbilities',
    'occupationalStatus',
    // 'nifEmployerEntity',
    // 'employerEntity',
    // 'admissionDate',
    // 'companyAddress',
    // 'companyDoorNumber',
    // 'companyFloorNumber',
    // 'companyCountry',
    // 'companyProvince',
    // 'companyCounty',
    // 'companyZipCode',
    // 'occupationalStatusProof',
    'profession',
    // 'contractualBond',
    'companyFunction',
    'monthlyIncome',
    'monthlyExpenses',
    'corporateInterests'
    // 'shareHoldingsList'
])

const generalInfoMandatoryFields = computed(() => {
    let tempMandatoryList = [...mandatoryList.value]
    /* Adding options from mandatory fields based on occupationalStatus */
    if (generalInfoForm.value.occupationalStatus === 1) { /* Student */
        tempMandatoryList.push('occupationalStatusProof')
    } else if (generalInfoForm.value.occupationalStatus === 2) { /* Sel fEmployed */
        tempMandatoryList.push('occupationalStatusProof')
    } else if (generalInfoForm.value.occupationalStatus === 3) { /* OtherEmployed */
        tempMandatoryList = [
            ...tempMandatoryList,
            'occupationalStatusProof',
            'nifEmployerEntity',
            'employerEntity',
            'admissionDate',
            'companyAddress',
            'companyDoorNumber',
            'companyFloorNumber',
            'companyCountry',
            'occupationalStatusProof',
            'contractualBond'
        ]

        if (generalInfoForm.value.companyCountry === 'Angola') {
            tempMandatoryList = [
                ...tempMandatoryList,
                'companyProvince',
                'companyCounty',
                'companyZipCode'
            ]
        }
    } else if (generalInfoForm.value.occupationalStatus === 4) {
        tempMandatoryList = tempMandatoryList.filter(item => item !== 'companyFunction')
    }

    if (generalInfoForm.value.corporateInterests === 'Yes') tempMandatoryList.push('shareHoldingsList')

    return tempMandatoryList
})

const showMandatoryErrorsHandler = () => {
    /* Logic to scroll to first emptyField */
	const mandatoryFieldsEmptyList = getEmptyFieldsIdentifiers(generalInfoForm.value, generalInfoMandatoryFields.value)
    if (mandatoryFieldsEmptyList.length) {
        /* 90px is for sideModal header curtain */
        scrollValue.value = getElementOffsetTopPosition(mandatoryFieldsEmptyList[0]) - 90

        mandatoryFieldsEmptyList.forEach(fieldName => {
            allFormFields.value[`${fieldName}Params`].errorState = true
            allFormFields.value[`${fieldName}Params`].errorMessage = t('mandatoryFieldError')
        })
    } else {
        emit('updateModalInfo', { info: generalInfoForm.value, subitemId: 'generalInfo' })
        triggerClose.value = true
    }
}

/**********************************************************************
*                                                                     *
*                           Clear Field Errors                        *
*                                                                     *
***********************************************************************/
const clearFieldError = (fieldName) => {
    allFormFields.value[fieldName].errorMessage = null
    allFormFields.value[fieldName].errorState = false
}

/**********************************************************************
*                                                                     *
*                                Footer                               *
*                                                                     *
***********************************************************************/
const buttonsFooterData = [{
	label: t('join.createAccount.step4.saveBtn'),
	buttonType: 'primary',
	callbackFunction: showMandatoryErrorsHandler
}]
</script>

<style lang="scss" scoped>
.status-proof-wrapper {
    width: 100%;

    display: flex;
    flex-direction: column;

    .title {
        width: 100%;
        margin-bottom: 8px;

        @include H7-Medium;
        color: var(--secondary-dark-2);
    }

    .instructions {
        width: 100%;
        margin-bottom: 24px;

        @include H7-Regular;
        color: var(--secondary-dark-1);
    }
}

.corporate-interest-list-wrapper {
    width: 100%;
    margin-bottom: 24px;

    display: flex;
    flex-direction: column;

    > * {
        &:not(.prevent-auto-margins) {
            width: calc(100% - 32px);
            margin-left: auto;
            margin-right: auto;
        }
    }

    .instructions {
        margin-top: 8px;
        margin-bottom: 16px;

        @include H7-Regular;
        color: var(--secondary-dark-1);
    }

    .button-secondary {
        margin-top: 24px;
    }

    .error-message {
        margin-top: 8px;

        @include H7-Regular;
        line-height: 16px;
        text-align: left;
        color: var(--negative);
    }
}
</style>
